import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Edit, Trash2 } from 'lucide-react';
import { Cleanup } from 'src/trpc';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal } from 'lucide-react';
import { EditCleanupModal } from './EditCleanupModal';
import { trpc } from 'src/trpc';
import { useToast } from 'components/useToast';
import { FC, useState } from 'react';
import { ConfirmDialog } from 'src/components/ConfirmDialog';

export const CleanupColumns: ColumnDef<Cleanup>[] = [
  {
    accessorKey: 'title',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Title
        </Button>
      );
    },
    cell: ({ cell }) => {
      const value = cell.getValue() as string;
      return <div className="font-medium">{value}</div>;
    },
  },
  {
    accessorKey: 'descriptionItems',
    header: 'Description Items',
    cell: ({ cell }) => {
      const items = cell.getValue() as string[];
      return (
        <div className="max-w-md">
          <ul className="list-disc list-inside space-y-1">
            {items.map((item, index) => (
              <li key={index} className="text-sm text-gray-600 truncate">
                {item}
              </li>
            ))}
          </ul>
        </div>
      );
    },
  },
  {
    accessorKey: 'isTemplate',
    header: 'Template',
    cell: ({ cell }) => {
      const value = cell.getValue() as boolean;
      return (
        <div className="w-fit">
          <Badge variant={value ? 'default' : 'secondary'}>{value ? 'Yes' : 'No'}</Badge>
        </div>
      );
    },
  },
  {
    accessorKey: 'archived',
    header: 'Status',
    cell: ({ cell }) => {
      const value = cell.getValue() as boolean;
      return (
        <div className="w-fit">
          <Badge variant={value ? 'destructive' : 'default'}>{value ? 'Archived' : 'Active'}</Badge>
        </div>
      );
    },
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Created
        </Button>
      );
    },
    cell: ({ cell }) => {
      const value = cell.getValue() as Date;
      return (
        <div className="text-sm text-gray-500">
          {value ? new Date(value).toLocaleDateString() : 'N/A'}
        </div>
      );
    },
  },
  {
    accessorKey: 'position',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Position
        </Button>
      );
    },
    cell: ({ cell }) => {
      const value = cell.getValue() as number;
      return (
        <div className="w-fit">
          <Badge variant="secondary">{value || 'N/A'}</Badge>
        </div>
      );
    },
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const cleanup = row.original;

      return <CleanupActions cleanup={cleanup} />;
    },
  },
];

// Separate component for actions to handle mutations
const CleanupActions: FC<{ cleanup: Cleanup }> = ({ cleanup }) => {
  const utils = trpc.useUtils();
  const [openArchiveDialog, setOpenArchiveDialog] = useState(false);
  const { successToast, errorToast } = useToast();

  const archiveMutation = trpc.admin.cleanup.archiveCleanup.useMutation({
    onSuccess: () => {
      successToast({ title: 'Cleanup archived successfully' });
      utils.admin.cleanup.getCleanups.invalidate();
    },
    onError: (error) => {
      errorToast({ title: 'Failed to archive cleanup', description: error.message });
    },
  });

  const unarchiveMutation = trpc.admin.cleanup.unarchiveCleanup.useMutation({
    onSuccess: () => {
      successToast({ title: 'Cleanup unarchived successfully' });
      utils.admin.cleanup.getCleanups.invalidate();
    },
    onError: (error) => {
      errorToast({ title: 'Failed to unarchive cleanup', description: error.message });
    },
  });

  const handleArchive = () => {
    setOpenArchiveDialog(true);
  };

  const confirmArchive = async () => {
    if (cleanup.archived) {
      await unarchiveMutation.mutateAsync(cleanup._id.toString());
    } else {
      await archiveMutation.mutateAsync(cleanup._id.toString());
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <EditCleanupModal
            cleanup={cleanup as Cleanup}
            trigger={
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
            }
          />
          <DropdownMenuItem
            onClick={handleArchive}
            disabled={unarchiveMutation.isPending}
            className="text-red-600"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {cleanup.archived ? 'Unarchive' : 'Archive'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={openArchiveDialog}
        onOpenChange={setOpenArchiveDialog}
        title={cleanup.archived ? 'Unarchive Cleanup' : 'Archive Cleanup'}
        description={`Are you sure you want to ${cleanup.archived ? 'unarchive' : 'archive'} this cleanup?`}
        confirmText={cleanup.archived ? 'Unarchive' : 'Archive'}
        onConfirm={confirmArchive}
        isLoading={archiveMutation.isPending || unarchiveMutation.isPending}
      />
    </>
  );
};
